import { StatsigExperiments, StatsigTreatmentOrNull } from '@lib/services/experiments/experiments'
import { LocalStorageKey, getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from './local-storage'
import { day, month } from './time'
import { User } from '@lib/services/user/user'
import { getSubscriptionType, type SubscriptionType } from '@lib/services/user/utils'

export interface StoredToolUsageData {
  count: number
  expirationTimestamp: number
}

export interface ToolUsageStateTracker {
  canUse: boolean
  currentUsage: number
  maxUsages: number
  resetsAt: Date | null
}

// Helper to get current URL path on client side with fallback
export const getCurrentUrlPath = (defaultToolType = 'default-tool'): string => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.pathname
  }
  return defaultToolType
}

// Storage abstraction interface for future flexibility
export interface StorageAdapter {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

// Default localStorage adapter
class PersistentStorageAdapter implements StorageAdapter {
  getItem(key: string): string | null {
    return getLocalStorageItem(key as LocalStorageKey)
  }

  setItem(key: string, value: string): void {
    setLocalStorageItem(key as LocalStorageKey, value)
  }

  removeItem(key: string): void {
    removeLocalStorageItem(key as LocalStorageKey)
  }
}

export type SubscriptionLimits = {
  [K in SubscriptionType]?: number
}

export class UrlBasedUsageTracker extends EventTarget {
  private storageAdapter: StorageAdapter = new PersistentStorageAdapter()
  private maxUsages: number
  private toolUrl: string
  private isMonthly: boolean
  private subscriptionType: SubscriptionType
  private limitReachedListeners: Set<EventListenerOrEventListenerObject> = new Set()

  constructor(
    limits: number | SubscriptionLimits = dailyToolsOptimalValueUsageLimits['high_usage_limit'],
    isMonthly: boolean = false,
    toolUrl: string = getCurrentUrlPath(),
    user?: User
  ) {
    super()
    this.toolUrl = toolUrl
    this.isMonthly = isMonthly
    this.subscriptionType = user ? getSubscriptionType(user) : 'anonymous'
    this.maxUsages =
      typeof limits === 'number'
        ? limits
        : (limits[this.subscriptionType] ?? dailyToolsOptimalValueUsageLimits['high_usage_limit'])
  }

  override addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (!listener) {
      return
    }
    super.addEventListener(type, listener, options)

    if (type === 'limitreached') {
      this.limitReachedListeners.add(listener)
    }
  }

  override removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions
  ): void {
    if (!listener) {
      return
    }
    super.removeEventListener(type, listener, options)

    if (type === 'limitreached') {
      this.limitReachedListeners.delete(listener)
    }
  }

  hasOnLimitReachedCallback(): boolean {
    return this.limitReachedListeners.size > 0
  }

  checkAndTriggerLimitReached(): boolean {
    const state = this.checkToolUsageState()
    if (!state.canUse) {
      this.dispatchEvent(new Event('limitreached'))
      return true
    }
    return false
  }

  private getStorageKeys() {
    // Create safe key names from URL
    const urlKey = this.toolUrl.replace(/[^a-zA-Z0-9]/g, '_')

    return {
      countKey: `toolUsage_count_${urlKey}_${this.subscriptionType}`,
      expirationKey: `toolUsage_expiration_${urlKey}_${this.subscriptionType}`,
    }
  }

  private getStoredToolUsageData(): StoredToolUsageData {
    const { countKey, expirationKey } = this.getStorageKeys()

    const countStr = this.storageAdapter.getItem(countKey)
    const expirationStr = this.storageAdapter.getItem(expirationKey)

    const count = countStr ? Number(countStr) : 0
    const expirationTimestamp = expirationStr ? Number(expirationStr) : 0

    return {
      count: isNaN(count) ? 0 : count,
      expirationTimestamp: isNaN(expirationTimestamp) ? 0 : expirationTimestamp,
    }
  }

  private clearExpiredToolUsageData(): void {
    const { countKey, expirationKey } = this.getStorageKeys()
    this.storageAdapter.removeItem(countKey)
    this.storageAdapter.removeItem(expirationKey)
  }

  checkToolUsageState(): ToolUsageStateTracker {
    const usageData = this.getStoredToolUsageData()
    const now = Date.now()

    // If expired, clear the usage data
    if (now > usageData.expirationTimestamp) {
      this.clearExpiredToolUsageData()
      return {
        canUse: true,
        currentUsage: 0,
        maxUsages: this.maxUsages,
        resetsAt: null,
      }
    }

    return {
      canUse: usageData.count < this.maxUsages,
      currentUsage: usageData.count,
      maxUsages: this.maxUsages,
      resetsAt: new Date(usageData.expirationTimestamp),
    }
  }

  incrementToolUsageState(): ToolUsageStateTracker {
    const { countKey, expirationKey } = this.getStorageKeys()
    const usageData = this.getStoredToolUsageData()
    const now = Date.now()

    // If expired, start fresh
    if (now > usageData.expirationTimestamp) {
      const newExpiration = now + (this.isMonthly ? month : day) * 1000
      this.storageAdapter.setItem(countKey, '1')
      this.storageAdapter.setItem(expirationKey, newExpiration.toString())

      const state = {
        canUse: 1 < this.maxUsages,
        currentUsage: 1,
        maxUsages: this.maxUsages,
        resetsAt: new Date(newExpiration),
      }

      this.dispatchEvent(new Event('usage'))
      return state
    }

    // Increment existing usage
    const newCount = usageData.count + 1
    this.storageAdapter.setItem(countKey, newCount.toString())

    const state = {
      canUse: newCount < this.maxUsages,
      currentUsage: newCount,
      maxUsages: this.maxUsages,
      resetsAt: new Date(usageData.expirationTimestamp),
    }

    this.dispatchEvent(new Event('usage'))
    if (!state.canUse) {
      this.dispatchEvent(new Event('limitreached'))
    }

    return state
  }

  resetToolUsageState(): void {
    this.clearExpiredToolUsageData()
  }

  setMaxToolUsage(maxUsages: number): void {
    this.maxUsages = maxUsages
  }
}

export const dailyToolsOptimalValueUsageLimits = {
  low_usage_limit: 1,
  medium_usage_limit: 3,
  high_usage_limit: 100,
}

export const toolsFreeUseLimitToTrialLimits = {
  limit_3: 3,
  limit_5: 5,
} as const

export type FreeTrialUsageTreatment = keyof typeof toolsFreeUseLimitToTrialLimits

export const isToolsOptimalUsageLimitTestTreatment = (
  toolsOptimalUsageLimitAssignment: StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsOptimalUsageLimit>
) => {
  return (
    toolsOptimalUsageLimitAssignment &&
    ['low_usage_limit', 'medium_usage_limit', 'high_usage_limit'].includes(toolsOptimalUsageLimitAssignment)
  )
}

export const getExpOptimalUsageLimit = (
  toolsOptimalUsageLimitAssignment: StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsOptimalUsageLimit>,
  controlUsageLimit: number
) => {
  return isToolsOptimalUsageLimitTestTreatment(toolsOptimalUsageLimitAssignment)
    ? dailyToolsOptimalValueUsageLimits[
        toolsOptimalUsageLimitAssignment as keyof typeof dailyToolsOptimalValueUsageLimits
      ]
    : controlUsageLimit
}

export const isToolsFreeUseLimitToTrialTestTreatment = (
  toolsFreeUseLimitAssignment: StatsigTreatmentOrNull<typeof StatsigExperiments.ToolsFreeUseLimitToTrial>
): toolsFreeUseLimitAssignment is FreeTrialUsageTreatment => {
  return toolsFreeUseLimitAssignment !== null && toolsFreeUseLimitAssignment in toolsFreeUseLimitToTrialLimits
}

// G2A-2051 Tools LTV Exp 3
export const toolsLtvExp3Limits = {
  limit_1: 1,
  limit_2: 2,
} as const
